import * as Sentry from '@sentry/react'

import { getToken } from './tokenManager'
import { isEmpty } from './utils'

export type RequestHeader = {
    'Content-Type': string
    Accept: string
    Authorization?: string
}

export type Request = {
    method: string
    headers: RequestHeader
    body?: string
}

export const REQUEST_TYPES = {
    post: 'POST',
    get: 'GET',
    put: 'PUT',
    patch: 'PATCH',
    delete: 'DELETE',
}

export default async function makeRequest(
    url: string,
    method = 'GET',
    body: string | null = null
): Promise<any> {
    try {
        const headers: RequestHeader = {
            'Content-Type': 'application/json',
            Accept: 'application/vnd.api+json',
        }

        if (!isEmpty(getToken())) {
            headers.Authorization = `Bearer ${getToken()}`
        }

        const request: Request = {
            method,
            headers,
        }

        if (body !== null) {
            request.body = body
        }

        const response = await fetch(url, request)

        // The body is read as text first because a proxy or an unhandled
        // server error can answer with HTML, and parsing that blindly hides
        // the status code behind a SyntaxError.
        const text = await response.text()
        let data: any = null
        if (text.length > 0) {
            try {
                data = JSON.parse(text)
            } catch {
                data = undefined
            }
        }

        if (!response.ok) {
            if (data?.error) {
                throw data.error
            }
            if (data === undefined || data === null) {
                throw new Error(`Request failed with status ${response.status}`)
            }

            return data
        }

        if (data === undefined) {
            throw new Error(
                `Unexpected non-JSON response with status ${response.status}`
            )
        }

        return data
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
        Sentry.captureException(error)
        throw error
    }
}
